import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { Dispatch, RootState } from '../../store/store';
import Modal from '../Modal/Modal';
import ControllerSearchSelect from '../ControllerSearchSelect/ControllerSearchSelect';

type OwnProps = {
  isVisible: boolean;
  onClose: () => void;
  controllerSetupId: string | null;
};

type ComponentProps = OwnProps & ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

// A checkbox that defaults to unchecked can be applied without the user ever
// consciously deciding either way. These two questions must be answered
// explicitly - Apply stays disabled until both are - so overwriting channel
// names/current scaling is always a deliberate choice, never a silent default.
type YesNo = 'yes' | 'no' | null;

const ApplyControllerSetupModal: React.FC<ComponentProps> = ({
  isVisible,
  onClose,
  controllerSetupId,
  controllers,
  getControllers,
  applyControllerSetup,
}) => {
  const [selectedControllerIds, setSelectedControllerIds] = useState<string[]>([]);
  const [overwriteChannelNames, setOverwriteChannelNames] = useState<YesNo>(null);
  const [overwriteCurrentScaling, setOverwriteCurrentScaling] = useState<YesNo>(null);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (isVisible) {
      getControllers();
    }
  }, [isVisible]);

  const resetAndClose = () => {
    setSelectedControllerIds([]);
    setOverwriteChannelNames(null);
    setOverwriteCurrentScaling(null);
    onClose();
  };

  const canSubmit =
    !isApplying && selectedControllerIds.length > 0 && overwriteChannelNames !== null && overwriteCurrentScaling !== null;

  const onApplyHandler = async () => {
    if (!controllerSetupId || !canSubmit) return;

    setIsApplying(true);
    const results = await applyControllerSetup({
      id: controllerSetupId,
      payload: {
        controllerIds: selectedControllerIds,
        overwriteChannelNames: overwriteChannelNames === 'yes',
        overwriteCurrentScaling: overwriteCurrentScaling === 'yes',
      },
    });
    setIsApplying(false);

    if (!results) return;

    const succeeded = results.filter((item) => item.success).length;
    const failed = results.length - succeeded;

    if (succeeded) {
      toast.success(`Applied to ${succeeded} controller${succeeded > 1 ? 's' : ''}`);
    }
    if (failed) {
      toast.error(`Failed to apply to ${failed} controller${failed > 1 ? 's' : ''}`);
    }

    resetAndClose();
  };

  return (
    <Modal
      isVisible={isVisible}
      modalTitle="Apply Controller Setup"
      onClose={resetAndClose}
      onAccept={onApplyHandler}
      acceptBtnText={isApplying ? 'Applying...' : 'Apply'}
      acceptBtnDisabled={!canSubmit}
      cancelBtnDisabled={isApplying}
    >
      <div className="flex w-full flex-col gap-4 text-left">
        <div>
          <p className="mb-2 font-medium text-black dark:text-white">Select controller(s)</p>
          <ControllerSearchSelect
            controllers={controllers}
            selectedIds={selectedControllerIds}
            onChange={setSelectedControllerIds}
            multiple
          />
        </div>
        <div>
          <p className="mb-2 font-medium text-black dark:text-white">
            Overwrite the channel names already on the selected controller(s)?
          </p>
          <div className="flex gap-5">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="overwriteChannelNames"
                checked={overwriteChannelNames === 'yes'}
                onChange={() => setOverwriteChannelNames('yes')}
              />
              Yes
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="overwriteChannelNames"
                checked={overwriteChannelNames === 'no'}
                onChange={() => setOverwriteChannelNames('no')}
              />
              No
            </label>
          </div>
        </div>
        <div>
          <p className="mb-2 font-medium text-black dark:text-white">
            Overwrite the current scaling settings already on the selected controller(s)?
          </p>
          <div className="flex gap-5">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="overwriteCurrentScaling"
                checked={overwriteCurrentScaling === 'yes'}
                onChange={() => setOverwriteCurrentScaling('yes')}
              />
              Yes
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="overwriteCurrentScaling"
                checked={overwriteCurrentScaling === 'no'}
                onChange={() => setOverwriteCurrentScaling('no')}
              />
              No
            </label>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const mapState = (state: RootState) => ({
  controllers: state.controllers.controllers_list,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getControllers: dispatch.controllers.getControllers,
  applyControllerSetup: dispatch.controller_setups.applyControllerSetup,
});

export default connect(mapState, mapDispatch)(ApplyControllerSetupModal);
